// ==========================================
// src/pages/Home.jsx — CORREÇÃO: CLIENTES CARROSSEL + VALORES TONELADA + SEM DATAS
// ✅ Busca clientes com status='ativo'
// ✅ Clientes em carrossel com touch/swipe (desktop + mobile)
// ✅ Valores em tonelada: mostra R$ XX,XX / ton (sem valor total)
// ✅ REMOVIDO: datas de coleta e entrega dos cards
// ==========================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { 
  Truck, Package, Users, ArrowRight, MapPin, Shield,
  Search, Zap, TrendingUp, Star, Sun, Moon, Sunrise, Sunset,
  Award, Gem, Crown, Diamond, CheckCircle, Clock,
  Building2, Plus, Calendar, Phone, Mail, ChevronRight,
  Wallet, Route, BadgeCheck, Headphones, BarChart3,
  Gift, Trophy, Medal, PartyPopper,
  Loader2, Globe, ChevronLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { motion, useInView, AnimatePresence } from "framer-motion";

// ==========================================
// DETECTOR DE PERÍODO DO DIA
// ==========================================
const getPeriodoDoDia = () => {
  const hora = new Date().getHours();
  if (hora >= 6 && hora < 12) return 'manha';
  if (hora >= 12 && hora < 18) return 'tarde';
  return 'noite';
};

// ==========================================
// TEMAS POR PERÍODO
// ==========================================
const TEMAS = {
  manha: {
    nome: 'Manhã',
    icon: Sunrise,
    heroBg: 'from-sky-100 via-blue-50 to-white',
    heroText: 'text-slate-900',
    heroSub: 'text-slate-600',
    accent: 'bg-blue-600',
    accentHover: 'hover:bg-blue-700',
    accentText: 'text-blue-600',
    cardBg: 'bg-white',
    cardBorder: 'border-blue-100',
    statsBg: 'bg-gradient-to-r from-blue-600 to-blue-700',
    statsText: 'text-white',
    ctaBg: 'from-blue-600 to-blue-800',
    waveColor: '#ffffff',
    sombra: 'shadow-blue-100',
    botaoPrimario: 'bg-blue-600 hover:bg-blue-700 text-white',
    botaoSecundario: 'bg-white text-blue-600 border-2 border-blue-200 hover:bg-blue-50',
    tagline: 'Comece seu dia com as melhores cargas'
  },
  tarde: {
    nome: 'Tarde',
    icon: Sun,
    heroBg: 'from-orange-100 via-amber-50 to-yellow-50',
    heroText: 'text-slate-900',
    heroSub: 'text-slate-700',
    accent: 'bg-orange-500',
    accentHover: 'hover:bg-orange-600',
    accentText: 'text-orange-600',
    cardBg: 'bg-white',
    cardBorder: 'border-orange-100',
    statsBg: 'bg-gradient-to-r from-orange-500 to-amber-600',
    statsText: 'text-white',
    ctaBg: 'from-orange-500 to-amber-600',
    waveColor: '#ffffff',
    sombra: 'shadow-orange-100',
    botaoPrimario: 'bg-orange-500 hover:bg-orange-600 text-white',
    botaoSecundario: 'bg-white text-orange-600 border-2 border-orange-200 hover:bg-orange-50',
    tagline: 'Aproveite a tarde para maximizar seus ganhos'
  },
  noite: {
    nome: 'Noite',
    icon: Moon,
    heroBg: 'from-slate-900 via-blue-950 to-slate-900',
    heroText: 'text-white',
    heroSub: 'text-blue-200',
    accent: 'bg-blue-500',
    accentHover: 'hover:bg-blue-400',
    accentText: 'text-blue-300',
    cardBg: 'bg-slate-800/50',
    cardBorder: 'border-blue-800',
    statsBg: 'bg-gradient-to-r from-blue-800 to-slate-900',
    statsText: 'text-white',
    ctaBg: 'from-blue-800 via-slate-900 to-blue-950',
    waveColor: '#0f172a',
    sombra: 'shadow-blue-900/20',
    botaoPrimario: 'bg-blue-500 hover:bg-blue-400 text-white',
    botaoSecundario: 'bg-transparent text-blue-300 border-2 border-blue-500/50 hover:bg-blue-500/20',
    tagline: 'A noite também é hora de faturar'
  }
};

const estadosBrasil = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

// ==========================================
// BADGES
// ==========================================
function BadgeDisponivelReal() {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 animate-pulse">
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-ping"></span>
      Disponível
    </span>
  );
}

function BadgeDisponivelFicticia() {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"></span>
      Disponível para Análise
    </span>
  );
}

// ==========================================
// FORMATADOR DE VALOR — CORREÇÃO: TONELADA
// ==========================================
const formatarValorCarga = (carga) => {
  const unidade = (carga.peso_unidade || "").toLowerCase().trim();
  const peso = parseFloat(carga.peso_valor || carga.peso || 0);
  const valorTotal = parseFloat(carga.valor_total || carga.valor_frete || 0);
  
  // Se a unidade for tonelada, mostra preço por tonelada
  if (unidade === "ton" || unidade === "tonelada" || unidade === "toneladas") {
    if (valorTotal > 0 && peso > 0) {
      const valorPorTon = valorTotal / peso;
      return `R$ ${valorPorTon.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ton`;
    }
    return "A combinar";
  }
  
  // Para outras unidades, mantém o valor total normal
  if (!valorTotal) return "A combinar";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorTotal);
};

// ==========================================
// CARD DE CARGA — SEM DATAS
// ==========================================
function CardCargaHome({ carga, user, onVerDetalhes, onLoginClick }) {
  const isLogado = !!user;
  const isAprovado = user?.cadastro_aprovado === true;
  const isFicticia = carga.carga_ficticia === true;

  const formatarPeso = (peso, pesoUnidade) => {
    if (!peso) return "-";
    const num = parseFloat(peso.toString().replace(/[^\d.]/g, ""));
    if (isNaN(num)) return peso;
    const un = (pesoUnidade || "").toLowerCase().trim();
    if (un === "ton") return `${num} ton`;
    if (un === "kg") return `${num} kg`;
    if (un === "m3" || un === "m³" || un.includes("cub")) return `${num} m³`;
    if (num >= 1000) return `${(num / 1000).toFixed(1).replace(".0", "")} ton`;
    return `${num} kg`;
  };

  const handleClick = () => {
    if (!isLogado) { onLoginClick(); return; }
    if (!isAprovado) { toast.info("Aguardando aprovação do cadastro"); return; }
    onVerDetalhes(carga);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200">
              {carga.tipo_carga || "Carga"}
            </span>
            {isFicticia ? <BadgeDisponivelFicticia /> : <BadgeDisponivelReal />}
          </div>
          {isLogado && isAprovado && (
            <span className="text-sm font-bold text-blue-600">{formatarValorCarga(carga)}</span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm">{carga.origem_cidade} - {carga.origem_estado}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm">{carga.destino_cidade} - {carga.destino_estado}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100 text-xs">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Tipo de carga</p>
            <p className="font-semibold text-gray-800 mt-0.5">{carga.tipo_carga || "Não informado"}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Carroceria</p>
            <p className="font-semibold text-gray-800 mt-0.5">{carga.tipo_carroceria || carga.tipo_veiculo || "Não informada"}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">Peso</p>
            <p className="font-semibold text-gray-800 mt-0.5">{formatarPeso(carga.peso, carga.peso_unidade)}</p>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={handleClick} className="px-5 py-2 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all active:scale-[0.98]">
            Ver detalhes
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// CARGAS DISPONÍVEIS
// ==========================================
const CargasDisponiveisSection = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [cargas, setCargas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const buscar = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("cargas")
          .select("*")
          .eq("status", "disponivel")
          .order("created_at", { ascending: false })
          .limit(6);
        if (error) { console.error("Erro Supabase:", error); setCargas([]); }
        else { setCargas(data || []); }
      } catch (err) { console.error("Erro:", err); setCargas([]); }
      finally { setIsLoading(false); }
    };
    
    buscar();
    
    const channel = supabase
      .channel('cargas-home')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'cargas' },
        () => buscar()
      )
      .subscribe();
    
    return () => { supabase.removeChannel(channel); };
  }, []);

  const handleLoginClick = () => navigate("/login?from=" + encodeURIComponent(window.location.pathname));
  const handleVerTodas = () => navigate("/buscar-cargas");
  const handleVerDetalhes = (carga) => {
    if (!isAuthenticated) { handleLoginClick(); return; }
    navigate(`/carga/${carga.id}`);
  };

  if (!isLoading && cargas.length === 0) return null;
  if (isLoading) return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    </section>
  );

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6">
        <motion.div className="mb-8" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Fretes disponíveis agora</h2>
            </div>
          </div>
        </motion.div>

        <div className="space-y-3">
          {cargas.map((carga) => (
            <CardCargaHome key={carga.id} carga={carga} user={user} onVerDetalhes={handleVerDetalhes} onLoginClick={handleLoginClick} />
          ))}
        </div>

        <motion.div className="mt-8 text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <Button onClick={handleVerTodas} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold">
            Ver todas as cargas <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// ==========================================
// CONTADOR ANIMADO
// ==========================================
function AnimatedCounter({ target, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    let timer;
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [isInView, target]);
  
  return <span ref={ref}>{prefix}{count.toLocaleString("pt-BR")}{suffix}</span>;
}

// ==========================================
// PAINEL DE PRÊMIOS — NOMES DE MOTORISTAS
// ==========================================
const PainelPremios = () => {
  const [premioAtual, setPremioAtual] = useState(0);
  
  const motoristasPremio = [
    { nome: "João Batista", cidade: "Cuiabá, MT", valor: 3500, nivel: "Black", fretes: 267, icone: Crown },
    { nome: "Pedro Oliveira", cidade: "Rondonópolis, MT", valor: 2800, nivel: "Diamante", fretes: 156, icone: Diamond },
    { nome: "Carlos Eduardo", cidade: "Dourados, MS", valor: 2200, nivel: "Diamante", fretes: 134, icone: Gem },
    { nome: "Antônio Silva", cidade: "Goiânia, GO", valor: 1900, nivel: "Ouro", fretes: 89, icone: Star },
    { nome: "Lucas Ferreira", cidade: "Sinop, MT", valor: 1500, nivel: "Ouro", fretes: 67, icone: Award },
    { nome: "Marcos Santos", cidade: "Campo Grande, MS", valor: 1200, nivel: "Esmeralda", fretes: 45, icone: Medal },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPremioAtual((prev) => (prev + 1) % motoristasPremio.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const premio = motoristasPremio[premioAtual];
  const IconePremio = premio.icone;

  return (
    <section className="py-16 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 overflow-hidden relative">
      <div className="absolute inset-0 opacity-20">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-white/30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{ y: [0, -20, 0], rotate: [0, 360] }}
            transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}
          >
            <Star className="w-8 h-8" />
          </motion.div>
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
            <Trophy className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-lg">BoraFrete Prêmios</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Motoristas Ganharam esse Mês
          </h2>
          <p className="text-white/80">Prêmios de R$ 1.200 a R$ 3.500 por desempenho</p>
        </motion.div>

        <div className="max-w-md mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={premioAtual}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{ duration: 0.5 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border border-white/30 shadow-2xl"
            >
              <div className="flex items-center gap-4 mb-4">
                <motion.div 
                  className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <IconePremio className="w-8 h-8 text-amber-600" />
                </motion.div>
                <div>
                  <p className="text-white font-bold text-lg">{premio.nome}</p>
                  <p className="text-white/70 text-sm">{premio.cidade}</p>
                </div>
                <div className="ml-auto text-right">
                  <Badge className="bg-white/30 text-white border-0">
                    {premio.nivel}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between bg-white/10 rounded-xl p-4">
                <div>
                  <p className="text-white/60 text-xs uppercase">Prêmio recebido</p>
                  <motion.p 
                    className="text-3xl font-bold text-white"
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    R$ {premio.valor.toLocaleString('pt-BR')},00
                  </motion.p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-xs uppercase">Fretes</p>
                  <p className="text-xl font-bold text-white">{premio.fretes}</p>
                </div>
              </div>

              <div className="flex justify-center mt-4 gap-1">
                {motoristasPremio.map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-2 rounded-full transition-all ${i === premioAtual ? 'bg-white w-6' : 'bg-white/40 w-2'}`}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-white/80 text-sm mb-4">Quer aparecer aqui? Complete mais fretes e suba de nível!</p>
          <Link to="/cadastro-motorista">
            <Button className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-8 h-12">
              Quero Ganhar Prêmios <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// ==========================================
// BANNER CARGAS DESTAQUE — SEM DATAS
// ==========================================
const BannerCargasDestaque = ({ periodo, tema }) => {
  const [cargasDestaque, setCargasDestaque] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const buscarCargas = async () => {
      try {
        const { data, error } = await supabase
          .from("cargas")
          .select("id, titulo, origem_cidade, origem_estado, destino_cidade, destino_estado, valor_frete, valor_total, tipo_carga, peso_valor, peso_unidade")
          .eq("status", "disponivel")
          .order("valor_total", { ascending: false })
          .limit(3);
        
        if (error) throw error;
        setCargasDestaque(data || []);
      } catch (err) {
        console.error("Erro ao buscar cargas destaque:", err);
      } finally {
        setLoading(false);
      }
    };

    buscarCargas();
  }, []);

  const formatarPeso = (peso, unidade) => {
    if (!peso) return "";
    return `${peso} ${unidade || "ton"}`;
  };

  if (loading) {
    return (
      <div className={`${periodo === 'noite' ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} backdrop-blur-sm rounded-3xl p-6 border shadow-2xl`}>
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-12 h-12 rounded-xl ${tema.accent} flex items-center justify-center`}>
            <Star className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className={`font-bold ${tema.heroText} text-lg`}>Cargas em Destaque</p>
            <p className={`text-sm ${tema.heroSub}`}>Carregando...</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className={`p-4 rounded-xl ${periodo === 'noite' ? 'bg-white/5' : 'bg-slate-50'} animate-pulse h-16`} />
          ))}
        </div>
      </div>
    );
  }

  if (cargasDestaque.length === 0) {
    return (
      <div className={`${periodo === 'noite' ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} backdrop-blur-sm rounded-3xl p-6 border shadow-2xl`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl ${tema.accent} flex items-center justify-center`}>
            <Star className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className={`font-bold ${tema.heroText} text-lg`}>Cargas em Destaque</p>
            <p className={`text-sm ${tema.heroSub}`}>Melhores fretes disponíveis</p>
          </div>
        </div>
        <p className={`text-center py-8 ${tema.heroSub}`}>Nenhuma carga disponível no momento</p>
      </div>
    );
  }

  return (
    <div className={`${periodo === 'noite' ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} backdrop-blur-sm rounded-3xl p-6 border shadow-2xl`}>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl ${tema.accent} flex items-center justify-center`}>
          <Star className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className={`font-bold ${tema.heroText} text-lg`}>Cargas em Destaque</p>
          <p className={`text-sm ${tema.heroSub}`}>Melhores fretes disponíveis</p>
        </div>
      </div>

      <div className="space-y-3">
        {cargasDestaque.map((carga, index) => (
          <motion.div 
            key={carga.id}
            className={`p-4 rounded-xl ${periodo === 'noite' ? 'bg-white/10' : 'bg-slate-50'} border ${periodo === 'noite' ? 'border-white/10' : 'border-slate-100'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className={`text-sm font-bold ${tema.heroText}`}>
                    {carga.origem_cidade} → {carga.destino_cidade}
                  </p>
                  <Badge className={`text-[10px] ${carga.tipo_carga ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {carga.tipo_carga || "Carga"}
                  </Badge>
                </div>
                <p className={`text-xs ${tema.heroSub}`}>
                  {formatarPeso(carga.peso_valor, carga.peso_unidade)}
                </p>
              </div>
              <span className="text-green-500 font-bold text-sm ml-2">
                {formatarValorCarga(carga)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className={`mt-4 pt-4 border-t ${periodo === 'noite' ? 'border-white/10' : 'border-slate-100'}`}>
        <div className="flex justify-between items-center">
          <p className={`text-sm ${tema.heroSub}`}>Ver todas as cargas</p>
          <Link to="/buscar-cargas">
            <Button className={`text-sm ${tema.botaoPrimario} px-4 py-2 h-8`}>
              Buscar <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// CARROSSEL DE CLIENTES
// ==========================================
const ClientesCarousel = ({ clientes }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  const getItemsPerView = () => {
    if (typeof window === 'undefined') return 4;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 768) return 2;
    if (window.innerWidth < 1024) return 3;
    return 4;
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  useEffect(() => {
    const handleResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, clientes.length - itemsPerView);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    autoPlayRef.current = setInterval(nextSlide, 4000);
    return () => clearInterval(autoPlayRef.current);
  }, [nextSlide]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
    clearInterval(autoPlayRef.current);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    autoPlayRef.current = setInterval(nextSlide, 4000);
  };

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    clearInterval(autoPlayRef.current);
  };

  const handleTouchMove = (e) => {
    if (!startX) return;
    const x = e.touches[0].clientX;
    const diff = startX - x;
    carouselRef.current.scrollLeft = carouselRef.current.scrollLeft + diff;
    setStartX(x);
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    const diff = startX - endX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
    setStartX(0);
    autoPlayRef.current = setInterval(nextSlide, 4000);
  };

  if (clientes.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-blue-50 transition-colors hidden md:flex"
        disabled={currentIndex === 0}
      >
        <ChevronLeft className="w-5 h-5 text-gray-600" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-blue-50 transition-colors hidden md:flex"
        disabled={currentIndex >= maxIndex}
      >
        <ChevronRight className="w-5 h-5 text-gray-600" />
      </button>

      <div
        ref={carouselRef}
        className="overflow-hidden cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <motion.div
          className="flex gap-6"
          animate={{ x: -currentIndex * (100 / itemsPerView) + '%' }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {clientes.map((cliente, index) => (
            <motion.div
              key={cliente.id}
              className="flex-shrink-0"
              style={{ width: `calc(${100 / itemsPerView}% - ${(itemsPerView - 1) * 24 / itemsPerView}px)` }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <div className="group bg-slate-50 rounded-xl p-6 border border-slate-100 hover:shadow-lg hover:border-blue-200 transition-all duration-300 flex flex-col items-center justify-center min-h-[160px]">
                {cliente.logo_url ? (
                  <div className="w-full h-16 flex items-center justify-center mb-3">
                    <img 
                      src={cliente.logo_url} 
                      alt={cliente.nome}
                      className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                      draggable={false}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center"><span class="text-white font-bold text-lg">${cliente.nome.charAt(0).toUpperCase()}</span></div>`;
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-3">
                    <span className="text-white font-bold text-lg">
                      {cliente.nome.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <p className="text-sm font-semibold text-slate-700 text-center line-clamp-1 group-hover:text-blue-600 transition-colors">
                  {cliente.nome}
                </p>
                {cliente.descricao && (
                  <p className="text-xs text-slate-400 text-center mt-1 line-clamp-1">
                    {cliente.descricao}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div className="flex justify-center mt-6 gap-2">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300 w-2 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ==========================================
// SEÇÃO DE CLIENTES
// ==========================================
const ClientesSection = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const buscarClientes = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('clientes')
          .select('*')
          .eq('status', 'ativo')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setClientes(data || []);
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      } finally {
        setLoading(false);
      }
    };

    buscarClientes();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Nossos Clientes</h2>
            <p className="text-slate-500">Empresas que confiam na BoraFrete</p>
          </div>
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  if (clientes.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Nossos Clientes</h2>
          <p className="text-slate-500">Empresas que confiam na BoraFrete para seus fretes</p>
        </motion.div>

        <ClientesCarousel clientes={clientes} />
      </div>
    </section>
  );
};

// ==========================================
// HOME PRINCIPAL
// ==========================================
export default function Home() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [origem, setOrigem] = useState("");
  const [destino, setDestino] = useState("");
  const [periodo, setPeriodo] = useState(getPeriodoDoDia());

  const [clientesAtivos, setClientesAtivos] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);

  useEffect(() => {
    const buscarClientes = async () => {
      try {
        setLoadingClientes(true);
        const { data, error } = await supabase
          .from('clientes')
          .select('*')
          .eq('status', 'ativo')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setClientesAtivos(data || []);
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      } finally {
        setLoadingClientes(false);
      }
    };

    buscarClientes();
  }, []);

  const clienteDestaque = clientesAtivos.length > 0 ? clientesAtivos[0] : null;

  useEffect(() => {
    const interval = setInterval(() => {
      setPeriodo(getPeriodoDoDia());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const tema = TEMAS[periodo];
  const PeriodoIcon = tema.icon;

  const handleBuscar = () => {
    const params = new URLSearchParams();
    if (origem) params.append("origem", origem);
    if (destino) params.append("destino", destino);
    navigate(`/buscar-cargas${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* HERO */}
      <section className={`relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br ${tema.heroBg} transition-colors duration-1000`}>
        
        <div className="absolute inset-0 pointer-events-none">
          {periodo === 'manha' && (
            <>
              <div className="absolute top-20 right-20 w-64 h-64 bg-yellow-200/30 rounded-full blur-3xl" />
              <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl" />
            </>
          )}
          {periodo === 'tarde' && (
            <>
              <div className="absolute top-10 right-1/3 w-80 h-80 bg-orange-300/20 rounded-full blur-3xl" />
              <div className="absolute bottom-10 left-1/4 w-64 h-64 bg-amber-200/20 rounded-full blur-3xl" />
            </>
          )}
          {periodo === 'noite' && (
            <>
              <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl" />
              {[...Array(20)].map((_, i) => (
                <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse" style={{ 
                  top: `${Math.random() * 50}%`, 
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`
                }} />
              ))}
            </>
          )}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* LADO ESQUERDO — TEXTO */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${periodo === 'noite' ? 'bg-white/10 border border-white/20' : 'bg-white/80 border border-slate-200'} backdrop-blur-sm`}>
                <PeriodoIcon className={`w-4 h-4 ${tema.accentText}`} />
                <span className={`text-sm font-medium ${periodo === 'noite' ? 'text-blue-200' : 'text-slate-700'}`}>
                  Boa {tema.nome} • {new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>

              <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold ${tema.heroText} leading-tight mb-6`}>
                Motorista, <br />
                <span className={tema.accentText}>sua carga</span> <br />
                está aqui
              </h1>
              
              <p className={`text-xl ${tema.heroSub} mb-8 max-w-lg leading-relaxed`}>
                {tema.tagline}. Encontre fretes para sua rota e aumente seus ganhos hoje mesmo.
              </p>

              <div className={`${periodo === 'noite' ? 'bg-white/10 border-white/20' : 'bg-white border-slate-200'} backdrop-blur-sm rounded-2xl p-4 border shadow-xl`}>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="relative">
                    <MapPin className={`absolute left-3 top-3 w-5 h-5 ${periodo === 'noite' ? 'text-blue-300' : 'text-slate-400'}`} />
                    <Select value={origem} onValueChange={setOrigem}>
                      <SelectTrigger className={`pl-10 h-12 border-0 ${periodo === 'noite' ? 'bg-white/90' : 'bg-slate-50'}`}>
                        <SelectValue placeholder="Origem" />
                      </SelectTrigger>
                      <SelectContent>{estadosBrasil.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <MapPin className={`absolute left-3 top-3 w-5 h-5 ${periodo === 'noite' ? 'text-blue-300' : 'text-slate-400'}`} />
                    <Select value={destino} onValueChange={setDestino}>
                      <SelectTrigger className={`pl-10 h-12 border-0 ${periodo === 'noite' ? 'bg-white/90' : 'bg-slate-50'}`}>
                        <SelectValue placeholder="Destino" />
                      </SelectTrigger>
                      <SelectContent>{estadosBrasil.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleBuscar} className={`h-12 ${tema.botaoPrimario} font-bold text-lg shadow-lg`}>
                    <Search className="w-5 h-5 mr-2" />Buscar
                  </Button>
                </div>
              </div>

              <div className={`flex items-center gap-8 mt-8 ${periodo === 'noite' ? 'text-blue-200' : 'text-slate-500'}`}>
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  <span className="text-sm font-medium">180+ Cargas hoje</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  <span className="text-sm font-medium">Prêmios até R$ 3.500</span>
                </div>
              </div>
            </motion.div>

            {/* LADO DIREITO — CARGAS EM DESTAQUE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="hidden lg:block"
            >
              <BannerCargasDestaque periodo={periodo} tema={tema} />
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* STATS */}
      <section className={`py-16 ${tema.statsBg} transition-colors duration-1000`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Route, value: 180, suffix: "+", label: "Cargas Hoje" },
              { icon: Truck, value: 1800, suffix: "+", label: "Motoristas Ativos" },
              { icon: Gift, value: 3500, prefix: "R$ ", suffix: "", label: "Maior Prêmio" },
              { icon: BadgeCheck, value: 98, suffix: "%", label: "Satisfação" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }}>
                <motion.div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm border border-white/30" whileHover={{ scale: 1.1 }}>
                  <stat.icon className="w-7 h-7 text-white" />
                </motion.div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-2">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} />
                </p>
                <p className="text-white/80 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CARGAS DISPONÍVEIS */}
      <CargasDisponiveisSection />

      {/* PAINEL DE PRÊMIOS */}
      <PainelPremios />

      {/* PARA MOTORISTAS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Feito para Motoristas</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Tudo que você precisa para faturar mais na estrada</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Gift, title: "Ganhe Prêmios", desc: "Complete rotas e ganhe prêmios de R$ 1.200 a R$ 3.500. Quanto mais fretes, maior o prêmio!", cor: "bg-amber-500" },
              { icon: Route, title: "Escolha sua Rota", desc: "Filtre por origem, destino, tipo de carga e valor. Só aparece o que te interessa.", cor: "bg-blue-500" },
              { icon: Star, title: "Sem Mensalidade", desc: "Você não paga nada para usar. Só uma taxa de R$ 50,00 por contratação realizada.", cor: "bg-green-500" },
            ].map((item, i) => (
              <motion.div key={i} className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all border border-slate-100 group" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className={`w-14 h-14 ${item.cor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div className="flex flex-wrap gap-4 justify-center mt-12" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> SEM MENSALIDADE
            </div>
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> SÓ PAGA O QUE CARREGAR
            </div>
            <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> TAXA DE R$ 50,00
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">3 Passos para Faturar</h2>
            <p className="text-lg text-slate-600">De cadastro à estrada em minutos</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-green-400 via-blue-400 to-orange-400" />

            {[
              { num: "1", icon: Users, title: "Cadastre-se", desc: "Crie sua conta em 3 minutos. Receba atendimento personalizado com nossos atendentes." },
              { num: "2", icon: Search, title: "Escolha a Carga", desc: "Filtre por sua rota e veja todos os detalhes: valor, peso, tipo de veículo." },
              { num: "3", icon: CheckCircle, title: "Feche e Rode", desc: "Feche diretamente com Transportadora de Cargas. Totalmente Confiáveis e Seguras." },
            ].map((p, i) => (
              <motion.div key={i} className="relative text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200">
                  <span className="text-3xl font-bold text-white">{p.num}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-slate-600">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">O que os Motoristas Dizem</h2>
            <p className="text-lg text-slate-600">Histórias reais de quem fatura com a BoraFrete</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { nome: "Carlos Silva", cidade: "Cuiabá, MT", foto: "CS", nivel: "Ouro", fretes: 67, texto: "Antes ficava dentro dos Postos vendo escritórios de Transportadora. Agora abro o app e já sei onde tem frete. Ganhei mais tempo." },
              { nome: "Antônio Pereira", cidade: "Goiânia, GO", foto: "AP", nivel: "Diamante", fretes: 134, texto: "O contrato digital me deu segurança. Nunca mais fiquei sem receber. Pagamento direto com a transportadora sem erro." },
              { nome: "Marcos Oliveira", cidade: "Campo Grande, MS", foto: "MO", nivel: "Esmeralda", fretes: 23, texto: "Comecei há 5 meses e já fiz 23 fretes. A plataforma é muito fácil, até meu filho de 12 anos sabe usar." },
            ].map((d, i) => (
              <motion.div key={i} className="bg-slate-50 rounded-2xl p-8 border border-slate-100" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold">
                    {d.foto}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{d.nome}</p>
                    <p className="text-sm text-slate-500">{d.cidade}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-yellow-100 text-yellow-700">{d.nivel}</Badge>
                  <span className="text-sm text-slate-500">{d.fretes} fretes</span>
                </div>
                <p className="text-slate-700 italic">"{d.texto}"</p>
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEÇÃO DE CLIENTES — CARROSSEL */}
      <ClientesSection />

      {/* PARA EMPRESAS — CLIENTE DESTAQUE */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* LADO ESQUERDO — TEXTO */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Para Empresas Também</h2>
              <p className="text-slate-300 text-lg mb-8">
                Transportadoras, Fazendas, Confinamentos, Indústrias, Fábricas, Armazéns, Corretoras, Cooperativas, Usinas, Distribuidoras... Todos estão no BoraFrete.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span>Publique cargas em 1 minuto</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span>Escolha o motorista pela avaliação</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span>🚛 Fretes seguros e rápidos</span>
                </div>
              </div>
              <Link to="/cadastro-empresa" className="inline-block mt-8">
                <Button className="h-14 px-8 bg-blue-500 hover:bg-blue-400 text-white font-bold text-lg">
                  Publicar Carga <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* LADO DIREITO — CLIENTE DESTAQUE */}
            <div className="bg-slate-800 rounded-2xl p-8">
              {loadingClientes ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin mb-3" />
                  <p className="text-slate-400 text-sm">Carregando...</p>
                </div>
              ) : clienteDestaque ? (
                <>
                  <div className="flex flex-col items-center mb-6">
                    <img 
                      src={clienteDestaque.logo_url} 
                      alt={clienteDestaque.nome}
                      className="h-20 w-auto mb-4 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-20 h-20 bg-gradient-to-br from-[#FF6B35] to-[#FFA07A] rounded-2xl items-center justify-center">
                      <Building2 className="w-10 h-10 text-white" />
                    </div>
                    <p className="font-bold text-xl">{clienteDestaque.nome}</p>
                    <Badge className={`mt-2 ${clienteDestaque.status === 'ativo' ? 'bg-green-500' : 'bg-gray-500'}`}>
                      {clienteDestaque.status === 'ativo' ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {clienteDestaque.descricao ? (
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {clienteDestaque.descricao}
                      </p>
                    ) : (
                      <p className="text-slate-500 text-sm italic">
                        Sem descrição cadastrada
                      </p>
                    )}
                    
                    {clienteDestaque.website && (
                      <a 
                        href={clienteDestaque.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                        Visitar site
                      </a>
                    )}
                    
                    {clienteDestaque.telefone && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Phone className="w-4 h-4" />
                        {clienteDestaque.telefone}
                      </div>
                    )}
                    
                    {clienteDestaque.endereco && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <MapPin className="w-4 h-4" />
                        {clienteDestaque.endereco}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <Building2 className="w-16 h-16 mb-4 opacity-50" />
                  <p>Nenhum cliente em destaque</p>
                  <p className="text-sm mt-1">Adicione no painel admin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className={`relative py-24 bg-gradient-to-br ${tema.ctaBg} overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "30px 30px" }}></div>
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.h2 className="text-4xl md:text-5xl font-bold text-white mb-6" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            Pronto para começar a <span className="text-blue-300">transportar</span>?
          </motion.h2>
          <motion.p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} viewport={{ once: true }}>
            Cadastre-se gratuitamente e tenha acesso a milhares de cargas em todo o Brasil.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
            <Link to="/cadastro-motorista">
              <Button className="h-14 px-8 bg-white text-blue-900 hover:bg-blue-50 font-bold text-lg shadow-xl">
                Sou Motorista <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/cadastro-empresa">
              <Button className="h-14 px-8 bg-blue-500/20 text-white border-2 border-blue-400/50 hover:bg-blue-500/30 font-bold text-lg backdrop-blur-sm">
                Sou Empresa <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
          <motion.p className="mt-8 text-white/60 text-sm" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.4 }} viewport={{ once: true }}>
            Cadastro 100% gratuito • Sem mensalidade • Cancele quando quiser
          </motion.p>
        </div>
      </section>

    </div>
  );
}